import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";

// Assets
import purchaseIcon from "../../assets/down-arrow-blue.png";
import sellIcon from "../../assets/up-arrow-green.png";
import stockIcon from "../../assets/stock-icon-2.png";

// Utils
import { formatDate } from "../../utils/toDate";
import { UserAuth } from "../../utils/AuthContext";

// Components
import RoundedTable from "../../components/table-component/RoundedTable";
import PageWrapper from "../../components/PageWrapper";
import InformationBox from "../../components/InformationBox";
import MultipleSearchFilter from "../../components/MultipleSearchFilter";
import BalanceBoxes from "../../components/BalanceBoxes";
import Loader from "../../components/Loader";
import { TABLE_CELL_TYPES } from "../attendance-system/EmployeesAttendanceTable";
import PageHeadline from "../../components/PageHeadline";
import { toMoney } from "../../utils/toMoney";
import CopyButton from "../../components/CopyButton";

const PaperPage = () => {
  const { id } = useParams();

  const { state } = useLocation();
  const { colorCode, colorName } = state;

  // HOOKS
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);

  // STATES
  const [loading, setLoading] = useState(false);
  const [colorTransactions, setColorTransactions] = useState([]);
  const [colorData, setColorData] = useState({
    availableStock: 0,
    averageCost: 0,
    availableRollsInStock: 0,
  });
  const [selectedFilter, setSelectedFilter] = useState({
    title: t("colorStatus"),
    type: "dropdown",
    dropdown: {
      data: [
        { title: t("sell"), value: "SELL" },
        { title: t("purchase"), value: "PURCHASE" },
        { title: t("stock"), value: "STOCK" },
        { title: t("selectAll"), value: "" },
      ],
    },
  });

  const [searchQuery, setSearchQuery] = useState({
    title: t("selectAll"),
    value: "",
  });
  const [selectedOrderStatus, setSelectedOrderStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  // STATIC DATA
  const titles = [
    { value: t("Date"), type: TABLE_CELL_TYPES.DATE },
    { value: t("amountOfRolls"), type: TABLE_CELL_TYPES.TEXT },
    { value: t("quantity"), type: TABLE_CELL_TYPES.TEXT },
    { value: t("amount"), type: TABLE_CELL_TYPES.MONEY },
    { value: t("colorStatus"), type: TABLE_CELL_TYPES.TEXT },
  ];

  const filterOptions = [
    {
      title: t("colorStatus"),
      type: "dropdown",
      dropdown: {
        data: [
          { title: t("sell"), value: "SELL" },
          { title: t("purchase"), value: "PURCHASE" },
          { title: t("stock"), value: "STOCK" },
          { title: t("selectAll"), value: "" },
        ],
      },
    },
  ];

  // ENDPOINTS

  const getColorTransactions = async (filters = {}) => {
    setLoading(true);
    try {
      // params
      let params = {
        page: pagination.currentPage,
        limit: 5,
        orderType: searchQuery.value,
        organizationId: userData?.organizationId,
      };

      if (startDate) {
        params = {
          ...params,
          startDate: startDate,
        };
      }

      if (endDate) {
        params = {
          ...params,
          endDate: endDate,
        };
      }

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getAllFabricColorTransactions/${id}`,
        {
          params,

          headers: {
            "organization-id": userData?.organizationId,
          },
        }
      );

      const data = response.data;

      if (response.status === 200) {
        if (data.items && data.items.length > 0) {
          const fabricColorInfo = data.items[0].fabricColor;

          setColorData({
            availableStock: fabricColorInfo?.availableStock,
            averageCost: fabricColorInfo?.averageCost,
            availableRollsInStock: fabricColorInfo?.availableRollsInStock,
            notes: fabricColorInfo.notes,
          });
        }

        setPagination({
          currentPage: data.pagination.currentPage,
          totalPages: data.pagination.totalPages,
          totalRecords: data.pagination.totalRecords,
        });

        const formatedData = data.items.map((item) => {
          return [
            {
              value: formatDate(item.date),
              type: TABLE_CELL_TYPES.DATE,
            },
            {
              value: `${item?.amountOfRolls || 0} ${t("rollUnit")}`,
              type: TABLE_CELL_TYPES.TEXT,
            },
            {
              value: `${item.quantity || 0} ${t("ton")}`,
              type: TABLE_CELL_TYPES.TEXT,
            },
            {
              value: `${item.price?.toFixed(2)} ${
                item?.currency !== null
                  ? t(item.currency.toLowerCase())
                  : t("usd")
              }`,
              type: TABLE_CELL_TYPES.TEXT,
            },
            {
              value: (
                <div className="u-max-width_100 u-width_full-width u-margin-center o-row o-row--horizontal-align_space-between o-row--vertical-align_center">
                  <p>{t(item.FabricOrderType.toLowerCase())}</p>
                  <img
                    className="u-width-specific_20"
                    src={
                      item.FabricOrderType === "PURCHASE"
                        ? purchaseIcon
                        : item.FabricOrderType === "STOCK"
                        ? stockIcon
                        : sellIcon
                    }
                  />
                </div>
              ),
              type: TABLE_CELL_TYPES.ELEMENT,
            },
          ];
        });

        setColorTransactions(formatedData);
      }
    } catch (error) {
      console.error("Error fetching color data", error);
    } finally {
      setLoading(false);
    }
  };

  // HANDLERS

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));

    getColorTransactions();
  };

  // SIDE EFFECTS
  useEffect(() => {
    getColorTransactions();
  }, [selectedOrderStatus, pagination.currentPage]);

  return (
    <PageWrapper withBackButton>
      <div className="u-padding u-padding--bottom_700">
        <PageHeadline
          title={`${t("paperHistoryPage")}: ${colorName} `}
          classList="u-margin--top_700 u-margin--bottom_300 o-row o-row--gap-size_500"
        />
        <p className="o-row o-row--gap-size_100 o-row--vertical-align_center u-margin--top_0 u-margin--bottom_400 neutral-value">
          <CopyButton text={colorCode} />
          {colorCode}
        </p>

        <div className="u-margin u-margin--bottom_400">
          <MultipleSearchFilter
            options={filterOptions}
            selected={selectedFilter}
            setSelected={setSelectedFilter}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            handleSearch={handleSearch}
          />
        </div>

        {loading && <Loader />}

        {colorTransactions.length && !loading ? (
          <RoundedTable
            titles={titles}
            rows={colorTransactions}
            setPagination={setPagination}
            pagination={pagination}
          />
        ) : !loading ? (
          <InformationBox>{t("noTransactions")}</InformationBox>
        ) : (
          <></>
        )}

        <div className="o-column o-column--gap-size_300 u-padding--top_200 u-padding--bottom_500">
          <div className="o-row o-row--gap-size_300">
            <span className="t-bold_bold u-min-width-specific_100">
              {t("notes")}:
            </span>{" "}
            <span>{colorData?.notes}</span>
          </div>
        </div>

        <div>
          <BalanceBoxes
            boxes={[
              {
                title: "amountInStock",
                value: toMoney(colorData?.availableStock) + " " + t("ton"),
              },
              {
                title: "amountOfRolls",
                value:
                  toMoney(colorData?.availableRollsInStock) +
                  " " +
                  t("rollUnit"),
              },
              // {
              //   title: "averageCost",
              //   value:
              //     toMoney(colorData?.averageCost?.toFixed(2)) + " " + t("USD"),
              // },
            ]}
          />
        </div>
      </div>
    </PageWrapper>
  );
};

export default PaperPage;
